import React from "react";
import { array, option } from "fp-ts";
import { pipe } from "fp-ts/lib/function";
import { TContainerStateProps } from "../../state/TContainerStateProps";
import { createChangeRouteAction } from "../../state/router/createChangeRouteAction";
import { CRMPageWrap } from "../../components/Simple/CRMPageWrap/CRMPageWrap";
import { CRMLegalMenuBarContainer } from "../../containers/CRMLegalMenuBarContainer/CRMLegalMenuBarContainer"
import { Padding } from "../../components/BuildingBlocks/Padding";
import { SpacingRow } from "../../components/BuildingBlocks/SpacingRow";
import { CRMComposeEmailForm } from "../../components/CRM/CRMComposeEmail/CRMComposeEmailForm/CRMComposeEmailForm";
import { CRMSpacing } from "../../models/CRMSpacing";
import { CRMPrivateInbox } from "../../components/Complex/CRMPrivateInbox/CRMPrivateInbox";
import { CRMBlockScrollCacheView } from "../../components/CRM/CRMBlock/CRMBlockViews/CRMBlockViews";
import { CRMBlock } from "../../components/CRM/CRMBlock/CRMBlock/CRMBlock";
import { CRMLegalInboxRightPane } from "../../components/CRM/CRMLegalInboxRightPane/CRMLegalInboxRightPane";
import { CRMInboxCaseSummaryBlock } from "../../components/CRM/CRMInboxBlocks/CRMInboxCaseSummaryBlock";
import { TDetailedEmailForm, TTriageForm } from "../../../../domain/codecs/form/TriageForm";

const findOpenEmail = (forms: Array<TTriageForm>, openEmailId: string | null): TDetailedEmailForm | null => {
    if (openEmailId === null) {
        return null;
    }
    return pipe(
        forms,
        array.chain((form) => form.children.emails),
        array.findFirst((email) => email.original.id === openEmailId),
        option.map((email) => email.children.detailed_email_form),
        option.fold(
            () => null,
            (detailed) => detailed,
        ),
    );
};

const CRMLegalPrivateInboxPage = (props: TContainerStateProps): JSX.Element => {
    const inboxState = props.state.forms.inbox_assignee_page;
    const composeOverride = inboxState.right_column_compose_override;
    const openEmail = findOpenEmail(
        [inboxState.unresolved, inboxState.resolved, inboxState.confidential, inboxState.untriaged],
        inboxState.open_email_id,
    );

    return (
        <CRMPageWrap>
            {/* HEADER */}
            <CRMLegalMenuBarContainer {...props} />

            {/* BODY */}
            <Padding
                type="custom"
                spacing={`${CRMSpacing.X_LARGE} ${CRMSpacing.X_LARGE} 0px`}
            >
                <SpacingRow
                    spacing={CRMSpacing.X_LARGE}
                    alignItems="flex-start"
                >

                    {/* Staff inboxes live under Triage; this selector links to that view. */}
                    <CRMBlock
                        mode="select-options"
                        startingWidth={700}
                        selectedOption="my_inbox"
                        optionGroups={[{
                            name: "Inboxes",
                            options: {
                                my_inbox: { icon: "triangle", title: "My Inbox" },
                                staff_inboxes: { icon: "email-notification", title: "Staff Inboxes" },
                            },
                        }]}
                        onChange={(name) => {
                            if (name === "staff_inboxes") {
                                props.dispatch(createChangeRouteAction(
                                    "VIEW_CRM_TRIAGE",
                                    {},
                                    { tab: "staff_inboxes" },
                                ));
                            }
                        }}
                    >
                        <CRMPrivateInbox
                            onSearchTermChange={(payload) => props.dispatch({
                                type: "TRIAGE_ASSIGNEE_SEARCH_CHANGE",
                                payload,
                            })}
                            onChangeImportanceBand={(band) => props.dispatch({
                                type: "TRIAGE_ASSIGNEE_SET_BAND",
                                payload: band,
                            })}
                            unresolvedForm={props.state.forms.inbox_assignee_page.unresolved}
                            resolvedForm={props.state.forms.inbox_assignee_page.resolved}
                            confidentialForm={props.state.forms.inbox_assignee_page.confidential}
                            untriagedForm={props.state.forms.inbox_assignee_page.untriaged}
                            triageTypeVisible={props.state.forms.inbox_assignee_page.active_type_visible}
                            onChangeTriageTypeVisible={(payload) => props.dispatch({
                                type: "TRIAGE_ASSIGNEE_ACTIVE_TYPE_VISIBLE_CHANGE",
                                payload,
                            })}
                            onChangeSearchCaseQuery={(payload, triageType) => props.dispatch({
                                type: `TRIAGE_ASSIGNEE_${triageType}_SEARCH_CASE`,
                                payload,
                            })}
                            onOpenEmail={(payload, triageType) => props.dispatch({
                                type: `TRIAGE_ASSIGNEE_${triageType}_OPEN_EMAIL`,
                                payload,
                            })}
                            onLoadMore={(triageType) => props.dispatch({
                                type: `TRIAGE_ASSIGNEE_${triageType}_LOAD_MORE`,
                            })}
                            onAssignToCaseAndMe={(payload, triageType) =>
                                triageType === "UNRESOLVED" || triageType === "UNTRIAGED"
                                    ? props.dispatch({
                                        type: `TRIAGE_ASSIGNEE_${triageType}_ASSIGN_TO_CASE_AND_ME`,
                                        payload
                                    })
                                    : undefined
                            }
                            onAssignToCaseAndStaff={(payload, triageType) =>
                                triageType === "UNRESOLVED" || triageType === "UNTRIAGED"
                                    ? props.dispatch({
                                        type: `TRIAGE_ASSIGNEE_${triageType}_ASSIGN_TO_CASE_AND_STAFF`,
                                        payload
                                    })
                                    : undefined
                            }
                            onAssignToCaseAndHandler={(payload, triageType) =>
                                triageType === "UNRESOLVED" || triageType === "UNTRIAGED"
                                    ? props.dispatch({
                                        type: `TRIAGE_ASSIGNEE_${triageType}_ASSIGN_TO_CASE_AND_HANDLER`,
                                        payload
                                    })
                                    : undefined
                            }
                            onAssignToCaseAndResolve={(payload, triageType) =>
                                triageType === "UNRESOLVED" || triageType === "UNTRIAGED"
                                    ? props.dispatch({
                                        type: `TRIAGE_ASSIGNEE_${triageType}_ASSIGN_TO_CASE_AND_RESOLVE`,
                                        payload
                                    })
                                    : undefined
                            }
                            onAssignToPerson={(payload, triageType) =>
                                triageType === "UNRESOLVED" || triageType === "UNTRIAGED"
                                    ? props.dispatch({
                                        type: `TRIAGE_ASSIGNEE_${triageType}_ASSIGN_TO_PERSON`,
                                        payload
                                    })
                                    : undefined
                            }
                            onResolve={(payload) => props.dispatch({
                                type: `TRIAGE_ASSIGNEE_RESOLVE`,
                                payload
                            })}
                            onChangeChaseModeForm={(payload) => props.dispatch({
                                type: "TRIAGE_ASSIGNEE_RESOLVE_FORM_UPDATE_CHASE_MODE_FORM",
                                payload,
                            })}
                            onReply={(payload) => props.dispatch({
                                type: "CRM_COMPOSE_EMAIL_REPLY",
                                payload
                            })}
                            onForward={(payload) => props.dispatch({
                                type: "CRM_COMPOSE_EMAIL_FORWARD",
                                payload
                            })}
                            onFileAttachments={(payload) => {
                                const caseId = payload.children.email.currently_assigned_case_id;
                                if (!caseId) return;
                                props.dispatch({
                                    type: "CRM_COMPOSE_FILE_ATTACHMENTS",
                                    payload: { email: payload, case_id: caseId },
                                });
                            }}
                        />
                    </CRMBlock>

                    <CRMLegalInboxRightPane
                        assignmentState={props.state.forms.global_legal}
                        onCancel={() => props.dispatch({
                            type: "CRM_CLOSE_FILE_ATTACHMENTS",
                            payload: null,
                        })}
                        onCommit={(commit) => props.dispatch({
                            type: "CRM_FILE_ATTACHMENTS_COMMIT",
                            payload: {
                                case_id: props.state.forms.global_legal.file_attachments_assignment_case_id,
                                email_id: props.state.forms.global_legal.file_attachments_assignment_email.children.email.id,
                                commit,
                            },
                        })}
                    >
                        {/* BLOCK - CASE SUMMARY <-> COMPOSE EMAIL (user can swap; Reply/Forward auto-swaps) */}
                        <CRMBlock
                            mode="select-options"
                            selectedOption={composeOverride ? "compose_email" : "case_summary"}
                            optionGroups={[{
                                name: "Right pane",
                                options: {
                                    case_summary: { icon: "robot", title: "Case Summary" },
                                    compose_email: { icon: "send-email", title: "Compose Email" },
                                },
                            }]}
                            onChange={(name) => {
                                if (name === "case_summary" || name === "compose_email") {
                                    props.dispatch({
                                        type: "TRIAGE_ASSIGNEE_SET_RIGHT_PANE_COMPOSE",
                                        payload: name === "compose_email",
                                    });
                                }
                            }}
                        >
                            <CRMBlockScrollCacheView isShowing={composeOverride}>
                                <CRMComposeEmailForm
                                    closeMode="clear-only"
                                    apiUrl={env.REACT_APP_API_URL}
                                    templatesForm={props.state.forms.email_templates_form}
                                    composingUser={sessionUser}
                                    attachedEmail={props.state.forms.global_legal.attached_email}
                                    onChangeSearchCaseValue={(payload) => props.dispatch({
                                        type: "CRM_COMPOSE_EMAIL_TYPE_CASE_SEARCH_TEXT",
                                        payload,
                                    })}
                                    form={props.state.forms.global_legal.compose_email}
                                    onSelectCase={(payload) => props.dispatch({
                                        type: "CRM_SET_CASE_FOR_COMPOSE_EMAIL_FORM",
                                        payload,
                                    })}
                                    onChange={(payload) => props.dispatch({
                                        type: "CRM_UPDATE_COMPOSE_EMAIL_FORM",
                                        payload,
                                    })}
                                    onSubmit={(payload) => props.dispatch({
                                        type: "CRM_SEND_NEW_EMAIL",
                                        payload,
                                    })}
                                    onReply={(payload) => props.dispatch({
                                        type: "CRM_SEND_REPLY_EMAIL",
                                        payload,
                                    })}
                                    onForward={(payload) => props.dispatch({
                                        type: "CRM_SEND_FORWARD_EMAIL",
                                        payload,
                                    })}
                                    onClear={() => props.dispatch({
                                        type: "CRM_RESET_COMPOSE_EMAIL_FORM",
                                        payload: null,
                                    })}
                                    additionalSendToEmailAddresses={[]}
                                />
                            </CRMBlockScrollCacheView>
                            <CRMBlockScrollCacheView isShowing={!composeOverride}>
                                <CRMInboxCaseSummaryBlock openEmail={openEmail} />
                            </CRMBlockScrollCacheView>
                        </CRMBlock>
                    </CRMLegalInboxRightPane>
                </SpacingRow>
            </Padding>
        </CRMPageWrap>
    );
};

export default CRMLegalPrivateInboxPage;
